import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { supabase, isSupabaseReady } from "@/integrations/supabase/client";
import { linkTo, getImageUrl } from "@/lib/utils";
import StickyNavigation from "@/components/layout/StickyNavigation";
import TenantFooter from "@/components/layout/TenantFooter";
import { slugToSection, sectionToSlug, BLOG_SECTIONS, SECTION_COLOR_MAP, type BlogSection } from "@/lib/blogSections";
import LiveGoldForex from "@/components/blog/LiveGoldForex";
import StockMarketTicker from "@/components/blog/StockMarketTicker";

interface CardPost {
  slug: string;
  title: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  tags: string[];
  section: BlogSection;
}

export default function BlogSectionPage() {
  const { sectionSlug } = useParams();
  const section = useMemo(() => slugToSection(sectionSlug || ''), [sectionSlug]);

  const [posts, setPosts] = useState<CardPost[]>([]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        if (!isSupabaseReady || !section) return;
        const { data, error } = await supabase
          .from('blog_posts')
          .select('slug, title, excerpt, cover_image_url, author_name, section, published_at, tags')
          .eq('status', 'published')
          .eq('section', section)
          .order('published_at', { ascending: false });
        if (error) throw error;
        if (!ignore && data) {
          const mapped = (data as any[]).map((row) => ({
            slug: row.slug,
            title: row.title,
            excerpt: row.excerpt ?? '',
            cover: (row.cover_image_url && String(row.cover_image_url).trim()) || getImageUrl('placeholder.svg'),
            author: row.author_name ?? 'YC Tech Team',
            date: row.published_at ?? new Date().toISOString(),
            tags: Array.isArray(row.tags) ? row.tags : [],
            section: row.section || 'Business',
          })) as CardPost[];
          setPosts(mapped);
        }
      } catch {}
    }
    load();
    return () => { ignore = true; };
  }, [section]);

  if (!section) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <StickyNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-2xl font-semibold">Unknown section</h1>
          <p className="mt-2">Choose a section:</p>
          <nav className="mt-4 flex gap-4 text-sm">
            {BLOG_SECTIONS.map((s) => (
              <Link key={s} to={linkTo(`/chronicle/section/${sectionToSlug(s)}`)} className="text-blue-700 hover:underline">{s}</Link>
            ))}
          </nav>
        </div>
        <TenantFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <StickyNavigation />
      <style>{`
        /* Hide scrollbars on mobile overflow menus */
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Sections nav (top) */}
      <div className="sticky top-[64px] sm:top-[80px] z-40 border-b bg-gradient-to-b from-blue-50/90 to-blue-100/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="hidden md:flex items-center justify-center gap-x-4 md:gap-x-6 py-3 text-sm">
            <Link to={linkTo(`/chronicle`)} className="text-black hover:text-blue-700">Home</Link>
            <Link to={linkTo(`/chronicle/section/${sectionToSlug('Top Stories')}`)} className="text-black hover:text-blue-700">Top Stories</Link>
            {BLOG_SECTIONS.filter((s) => s !== 'Top Stories').map((s) => (
              <Link key={s} to={linkTo(`/chronicle/section/${sectionToSlug(s)}`)} className="text-black hover:text-blue-700">
                {s}
              </Link>
            ))}
          </nav>
          <nav className="md:hidden no-scrollbar flex gap-4 overflow-x-auto py-3 text-sm snap-x snap-mandatory -mx-4 px-4">
            <Link to={linkTo(`/chronicle`)} className="text-black whitespace-nowrap shrink-0 snap-start">Home</Link>
            <Link to={linkTo(`/chronicle/section/${sectionToSlug('Top Stories')}`)} className="text-black whitespace-nowrap shrink-0 snap-start">Top Stories</Link>
            {BLOG_SECTIONS.filter((s) => s !== 'Top Stories').map((s) => (
              <Link key={s} to={linkTo(`/chronicle/section/${sectionToSlug(s)}`)} className="text-black whitespace-nowrap shrink-0 snap-start">
                {s}
              </Link>
            ))}
          </nav>
        </div>
      </div>


      <section className="border-b" style={{ backgroundColor: SECTION_COLOR_MAP[section] || '#1F2937' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">{section}</h1>
          {section === 'Gold & Forex Rates' && (
            <div className="mt-6">
              <LiveGoldForex />
            </div>
          )}
        </div>
      </section>

      <section className="flex-1">
          {section === 'Stock Market' && (
            <div className="mb-6">
              <StockMarketTicker />
            </div>
          )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          <div className="grid items-stretch gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <article key={p.slug} className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden h-full flex flex-col">
                <Link to={linkTo(`/chronicle/${p.slug}`)} className="flex h-full flex-col">
                  <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                    <img
                      src={p.cover}
                      alt={p.title}
                      loading="lazy"
                      decoding="async"
                      fetchPriority="high"
                      className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        img.onerror = null;
                        img.src = getImageUrl('placeholder.svg');
                        img.style.display = 'block';
                      }}
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(p.tags || []).map((t) => (
                        <span key={t} className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-gray-600">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h3
                      className="text-lg font-semibold text-gray-900 group-hover:text-blue-700"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="mt-2 text-gray-600 text-sm"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {p.excerpt || ''}
                    </p>
                    <div className="mt-auto pt-3 sm:pt-4 flex items-center justify-between text-xs text-gray-500">
                      <span>{p.author}</span>
                      <span>{new Date(p.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <TenantFooter />
    </div>
  );
}

